import { useState } from "react";
import { ListChecks, Loader2, MousePointerClick, SquareCheckBig, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { WRICEF_DECISION_BULK_MAX, type WricefDecisionBulkResult } from "@/api/wricef-decisions";
import { BulkDecisionForm } from "@/components/agents/WricefDecisionSection";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { tp } from "@/lib/plural";

/**
 * A barra da decisão em lote (#1501), logo acima da grade do WRICEF Catalog. Só
 * o consultor a vê, como o formulário da decisão de um objeto.
 *
 * Diz quantos objetos estão marcados, em todas as páginas. Enquanto faltar
 * objeto do filtro, oferece marcar todos: a grade mostra 50 por página, e um
 * pacote inteiro pode passar disso. Acima do teto do lote, avisa em vez de
 * oferecer. "Apply decision" abre o formulário do lote.
 */
export function WricefBulkDecisionBar({
  projectId,
  selected,
  filterTotal,
  pageSelectable,
  selectingAll,
  onSelectAll,
  onClear,
  onApplied,
}: {
  projectId: string;
  /** Os objetos marcados, em todas as páginas. */
  selected: ReadonlySet<string>;
  /** Quantos objetos com letra o filtro tem: o máximo que dá para marcar. */
  filterTotal: number;
  /** Quantos objetos com letra a página mostra. */
  pageSelectable: number;
  selectingAll: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  /** Depois que o lote grava. */
  onApplied: (result: WricefDecisionBulkResult) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Os objetos do lote, guardados ao abrir: depois de gravar a seleção é
  // limpa, e o título e o resultado continuam falando dos que foram.
  const [batch, setBatch] = useState<string[]>([]);
  const count = selected.size;
  const max = WRICEF_DECISION_BULK_MAX;

  // Com qualquer seleção, e não só com a página toda marcada: marcar uma caixa
  // por vez ou pelo cabeçalho chega à mesma oferta.
  const offerAll = filterTotal > count;
  // Só quando a seleção passa da página: com tudo numa página só, a caixa do
  // cabeçalho já diz que está tudo marcado.
  const allOfFilter = count >= filterTotal && filterTotal > pageSelectable;

  return (
    <>
      {/* As duas formas têm a mesma altura: marcar o primeiro objeto não
          empurra a grade para baixo. */}
      {count === 0 ? (
        <div className="flex min-h-[48px] items-center gap-2 rounded-lg border border-dashed px-3 text-xs text-muted-foreground">
          <MousePointerClick className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("Select objects to decide in bulk")}
        </div>
      ) : (
        // O azul da decisão "em escopo" e o fundo da opção escolhida no
        // formulário: a barra diz "há uma seleção ativa".
        <div className="flex min-h-[48px] flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#c9d8f0] bg-[#f3f7fd] py-2 pl-3 pr-2 text-xs shadow-sm">
          <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--exed-ink-900)]">
            <SquareCheckBig className="h-4 w-4 text-[#1d4ea0]" aria-hidden="true" />
            {t("{{count}} selected", { count })}
          </span>
          {(offerAll || allOfFilter || count > max) && (
            <span aria-hidden="true" className="h-4 w-px bg-[#c9d8f0]" />
          )}
          {offerAll &&
            (filterTotal > max ? (
              <span className="text-muted-foreground">
                {t("The filter has {{n}} objects. A batch takes at most {{max}}.", {
                  n: filterTotal.toLocaleString("en-US"),
                  max,
                })}
              </span>
            ) : (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs font-medium text-[#1d4ea0]"
                onClick={onSelectAll}
                disabled={selectingAll}
              >
                {selectingAll && <Loader2 className="animate-spin" aria-hidden="true" />}
                {t("Select all {{n}} objects of the filter", { n: filterTotal.toLocaleString("en-US") })}
              </Button>
            ))}
          {allOfFilter && (
            <span className="text-muted-foreground">
              {t("All {{n}} objects of the filter are selected", { n: count.toLocaleString("en-US") })}
            </span>
          )}
          {count > max && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
              {t("A batch takes at most {{max}} objects.", { max })}
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2.5 text-xs text-muted-foreground hover:bg-[#e4ecf8] hover:text-[var(--exed-ink-900)]"
              aria-label={t("Clear selection")}
              onClick={onClear}
            >
              <X aria-hidden="true" />
              {t("Clear selection")}
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-[#1d4ea0] px-3.5 text-xs font-semibold text-white shadow-sm hover:bg-[#173f84]"
              disabled={count > max}
              onClick={() => {
                setBatch(Array.from(selected));
                setOpen(true);
              }}
            >
              <ListChecks aria-hidden="true" />
              {t("Apply decision")}
            </Button>
          </span>
        </div>
      )}

      {/* Fora da barra: a janela não some quando a seleção é limpa, e o
          resultado do lote fica na tela até o consultor fechar. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {tp(t, batch.length, "Apply decision to {{n}} object", "Apply decision to {{n}} objects", {
                n: batch.length.toLocaleString("en-US"),
              })}
            </DialogTitle>
            <DialogDescription>
              {t("Every selected object gets the same decision and the same criterion.")}
            </DialogDescription>
          </DialogHeader>
          <BulkDecisionForm
            projectId={projectId}
            objNames={batch}
            onSaved={onApplied}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
